import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { WhatsappLogo, List, X } from "@phosphor-icons/react";
import { useLang } from "@/lib/LanguageContext";
import { wa } from "@/lib/pfd";

const Header = ({ light = true }) => {
  const { t, lang, toggleLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const navLinks = [
    { id: "services", route: "/services", label: t.nav.services },
    { id: "business", route: "/corporate-concierge-paris", label: t.nav.business },
    { id: "chauffeur", route: "/private-chauffeur-paris", label: t.nav.chauffeur },
    { id: "about", route: "/about", label: t.nav.about },
    { id: "journal", route: "/journal", label: t.nav.journal },
    { id: "contact", route: "/contact", label: t.nav.contact },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (link) => {
    setOpen(false);
    navigate(link.route);
  };

  const onDark = light && !scrolled && !open;
  const ink = onDark ? "text-stone-50" : "text-stone-900";
  const inkSoft = onDark ? "text-stone-300" : "text-stone-500";
  const navInk = onDark ? "text-stone-200 hover:text-stone-50 after:bg-stone-50" : "text-stone-700 hover:text-stone-900 after:bg-stone-900";

  const LangToggle = ({ className = "", testId = "lang-toggle" }) => (
    <button
      data-testid={testId}
      onClick={toggleLang}
      className={`inline-flex items-center gap-1 text-xs font-medium tracking-[0.18em] transition-colors border px-3 py-2.5 ${
        onDark ? "border-stone-50/40 hover:border-stone-50 text-stone-50" : "border-stone-300 hover:border-stone-900 text-stone-700"
      } ${className}`}
      aria-label={`Switch language to ${lang === "fr" ? "English" : "Français"}`}
    >
      <span className={lang === "en" ? "opacity-100" : "opacity-40"}>EN</span>
      <span className="opacity-40">/</span>
      <span className={lang === "fr" ? "opacity-100" : "opacity-40"}>FR</span>
    </button>
  );

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
        onDark ? "bg-transparent" : "bg-stone-50/85 backdrop-blur-xl border-b border-stone-200"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 sm:h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="group flex flex-col leading-none">
          <span className={`font-serif text-xl sm:text-2xl tracking-tight transition-colors ${ink}`}>
            Book <span className={`italic ${inkSoft}`}>&amp;</span> Smile
          </span>
          <span className={`mt-1 text-[9px] sm:text-[10px] tracking-[0.28em] uppercase transition-colors ${inkSoft}`}>
            Paris · France
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {navLinks.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => handleNav(l)}
              className={`text-sm transition-colors relative after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 hover:after:w-full after:transition-all after:duration-300 ${navInk}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <LangToggle className="hidden sm:inline-flex" />
          <Link
            data-testid="header-consultation-cta"
            to="/contact"
            className={`hidden sm:inline-flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors ${
              onDark ? "bg-stone-50 text-slate-900 hover:bg-stone-200" : "bg-slate-900 text-stone-50 hover:bg-slate-800"
            }`}
          >
            {t.nav.headerCta}
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className={`lg:hidden p-3 -mr-3 ${ink}`}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <List size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-stone-50 border-t border-stone-200">
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-4">
            {navLinks.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-nav-${l.id}`}
                onClick={() => handleNav(l)}
                className="text-left text-stone-800 text-base py-2 border-b border-stone-200"
              >
                {l.label}
              </button>
            ))}
            <div className="flex items-center justify-between pt-2">
              <LangToggle testId="lang-toggle-mobile" />
              <a
                data-testid="mobile-whatsapp-cta"
                href={wa(t.home.waMsgPrimary)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-slate-900 text-stone-50 px-5 py-3 text-sm font-medium"
              >
                <WhatsappLogo size={16} weight="regular" /> {t.nav.mobileCta}
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
